// @givmo/compliance — the review's parts (CCM-R1-REVIEW-WEB): the per-item choices, the date block,
// the facts a result read, the opened basis, a source's evidence line (N24), the answer controls,
// and the quiet lines and sections every card shares.
//
// Every part is the module's existing idiom: the panel's grey date card (frames 03, 18), the send
// review's quiet lines and sections (frame 62), the source line (`RuleProvenanceLine`), the panel's
// month and day selects (`FiscalYearEndForm`).

import { Fragment, useState, type ReactNode } from 'react';
import {
  Button,
  Card,
  Checkbox,
  Dialog,
  Icon,
  Popover,
  Select,
  Textarea,
  cn,
  testIdProp,
} from '@givmo/design-system';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { US_JURISDICTIONS } from './compliance-decode';
import { INVITE_TO_COMPLIANCE } from './compliance-sending-model';
import { RuleProvenanceLine } from './compliance-shared';
import type { RuleComparison } from './compliance-rule-change';
import type { Basis, DateCard, EvidenceLine, FactLine, Mark } from './review-model';
import {
  ASK,
  ASK_ACCESS_LINE,
  ASK_A_TEAMMATE,
  ASK_NOTE,
  ASK_WHO,
  BASED_ON,
  BEFORE,
  CANCEL,
  CHOOSE_A_PERSON,
  NOW,
  USE_UPDATED_RULE_LINE,
  WHAT_CHANGED,
  alsoUpdatedRule,
  updatedRuleSameDates,
  CHOOSE_STATES,
  CORRECT,
  DAY,
  HIDE_HOW,
  MARK_CHANGED,
  MARK_NEW,
  MONTH,
  OUTCOME_WORDS,
  SHOW_HOW,
  evaluatedOn,
} from './review-words';

export const REVIEW_PREFIX = 'compliance-review';

/** A quiet text action, the send review's quiet-button idiom. */
export function TextAction({
  children,
  onClick,
  testId,
  disabled,
}: {
  children: ReactNode;
  onClick: () => void;
  testId?: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="rounded-xs text-s13 text-text-secondary underline decoration-border-strong underline-offset-2 hover:text-text-strong disabled:cursor-not-allowed disabled:opacity-50"
      {...testIdProp(testId)}
    >
      {children}
    </button>
  );
}

/** One quiet line of text actions and words, separated by dots (the panel's "27591 · Correct"). */
export function QuietLine({ children, testId }: { children: ReactNode[]; testId?: string }) {
  const parts = children.filter((part) => part !== null && part !== undefined && part !== false);
  return (
    <div
      className="flex flex-wrap items-baseline gap-x-6 gap-y-4 text-s13 text-text-secondary"
      {...testIdProp(testId)}
    >
      {parts.map((part, index) => (
        <span key={index} className="flex items-baseline gap-6">
          {index > 0 && <span aria-hidden>·</span>}
          {part}
        </span>
      ))}
    </div>
  );
}

/** A section of the review: its title, an action at its right, and its cards. */
export function ReviewSection({
  title,
  action,
  children,
  testId,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
  testId: string;
}) {
  return (
    <section className="flex flex-col gap-12" {...testIdProp(testId)}>
      <div className="flex items-baseline justify-between gap-12">
        <h2 className="text-s16 font-w600 text-text-strong">{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

/** One item's card. */
export function ItemCard({ children, testId }: { children: ReactNode; testId: string }) {
  return (
    <Card className="flex flex-col gap-12 px-20 py-16" testId={testId}>
      {children}
    </Card>
  );
}

/** "New" or "Changed" beside a title, in words (N33). */
export function MarkTag({ mark }: { mark: Mark | undefined }) {
  if (mark === undefined) return null;
  return (
    <span className="text-s12 font-w600 text-brand-700" {...testIdProp(`${REVIEW_PREFIX}-mark`)}>
      {mark === 'new' ? MARK_NEW : MARK_CHANGED}
    </span>
  );
}

export function CardTitle({ title, mark }: { title: string; mark?: Mark }) {
  return (
    <div className="flex items-baseline gap-8">
      {/* Balanced, so a title's last line never holds one word alone (cut 1008-125's walk). */}
      <h3 className="text-s15 font-w600 leading-h15 text-balance text-text-strong">{title}</h3>
      <MarkTag mark={mark} />
    </div>
  );
}

/**
 * An item's own choice: buttons of one size and weight, none chosen at first. The chosen one carries
 * a check and the brand's tint; pressing it again clears the choice.
 */
export function ChoiceButtons<T extends string>({
  options,
  value,
  onChange,
  testId,
}: {
  options: readonly { value: T; label: string }[];
  value: T | undefined;
  onChange: (next: T | undefined) => void;
  testId: string;
}) {
  return (
    <div className="flex flex-wrap gap-8" role="group" {...testIdProp(testId)}>
      {options.map((option) => {
        const chosen = option.value === value;
        return (
          <Button
            key={option.value}
            appearance="saas"
            tone="secondary"
            size="sm"
            aria-pressed={chosen}
            className={cn(
              chosen &&
                'border-brand-600 bg-brand-50 text-brand-700 hover:border-brand-600 hover:bg-brand-50',
            )}
            {...(chosen ? { leadingIcon: <Icon icon={Check} size={14} aria-hidden /> } : {})}
            onClick={() => onChange(chosen ? undefined : option.value)}
            testId={`${testId}-${option.value}`}
          >
            {option.label}
          </Button>
        );
      })}
    </div>
  );
}

/**
 * N37 and N38's line: what changed between the version a filing is pinned to and its rule's current
 * one (CCM-OPS1-REPIN's G4). One comparison, two ways in, as the frames draw it:
 * - the review's change card: a table (frame 101);
 * - the filing panel: stacked (frame 103).
 * The act under it is the caller's (`children`), in the same column: a choice saved with the
 * review's others, or the panel's act itself.
 */
export function WhatChanged({
  comparison,
  layout,
  repinLine = true,
  testId,
  children,
}: {
  comparison: RuleComparison;
  layout: 'table' | 'stacked';
  /** N38's line, under a comparison whose act is "Use the updated rule"; not under frame 105's. */
  repinLine?: boolean;
  testId: string;
  children?: ReactNode;
}) {
  // A row about a year names it under its label, so the label column widens to hold that line.
  const columns = comparison.rows.some((row) => row.qualifier !== undefined)
    ? 'grid-cols-[272px_minmax(0,1fr)_minmax(0,1fr)]'
    : 'grid-cols-[120px_minmax(0,1fr)_minmax(0,1fr)]';
  return (
    <div className="flex flex-col gap-12" {...testIdProp(testId)}>
      <p className="text-s13 font-w600 text-text-strong">{WHAT_CHANGED}</p>
      {comparison.rows.length === 0 ? (
        // U1-d: no date moved, so no table; the line says what did change.
        comparison.alsoChanged !== undefined && (
          <p className="text-s13 leading-h15 text-text" {...testIdProp(`${testId}-only`)}>
            {updatedRuleSameDates(comparison.alsoChanged)}
          </p>
        )
      ) : layout === 'stacked' ? (
        <div className="flex flex-col divide-y divide-border-subtle overflow-hidden rounded-lg border border-border-subtle bg-surface">
          {comparison.rows.map((row, index) => (
            <div key={index} className="flex flex-col gap-6 px-12 py-10 text-s13 leading-h15">
              <div className="flex flex-col gap-2">
                <p className="font-w600 text-text-strong">{row.label}</p>
                {row.qualifier !== undefined && (
                  <p className="text-s12 leading-h15 text-text-secondary">{row.qualifier}</p>
                )}
              </div>
              <p className="text-text-secondary">
                <span className="text-text-tertiary">
                  {BEFORE} · {comparison.pinnedOn}:{' '}
                </span>
                {row.before}
              </p>
              <p className="text-text">
                <span className="text-text-tertiary">
                  {NOW} · {comparison.currentOn}:{' '}
                </span>
                {row.now}
              </p>
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border-subtle">
          <div
            className={cn('grid bg-surface-sunken text-s12 font-w600 text-text-secondary', columns)}
          >
            <span className="px-12 py-8" />
            <span className="px-12 py-8">
              {BEFORE} · {comparison.pinnedOn}
            </span>
            <span className="px-12 py-8">
              {NOW} · {comparison.currentOn}
            </span>
          </div>
          {comparison.rows.map((row, index) => (
            <div
              key={index}
              className={cn('grid border-t border-border-subtle text-s13 leading-h15', columns)}
            >
              <span className="flex flex-col gap-2 px-12 py-10 text-text-secondary">
                {row.label}
                {row.qualifier !== undefined && (
                  <span className="text-s12 leading-h15 text-text-tertiary">{row.qualifier}</span>
                )}
              </span>
              <span className="px-12 py-10 text-text-secondary">{row.before}</span>
              <span className="px-12 py-10 text-text">{row.now}</span>
            </div>
          ))}
        </div>
      )}
      {comparison.rows.length > 0 && comparison.alsoChanged !== undefined && (
        // U1-c: what else the update changed, under the table.
        <p className="text-s13 leading-h15 text-text-secondary" {...testIdProp(`${testId}-also`)}>
          {alsoUpdatedRule(comparison.alsoChanged)}
        </p>
      )}
      {repinLine && (
        <p className="text-s12 leading-h15 text-text-tertiary">{USE_UPDATED_RULE_LINE}</p>
      )}
      {children}
    </div>
  );
}

/** Someone "Ask a teammate" may ask: a person who can make changes in Compliance. */
export interface Teammate {
  userId: string;
  name: string;
}

/** A refusal the server gave an ask, as served. */
export interface AskRefusal {
  line: string;
  /** The person chosen can't make changes in Compliance: the line is N42's own. */
  withoutAccess: boolean;
}

/**
 * N42's dialog (frame 93): who to ask, with the access line and the approved "Invite someone to
 * Compliance", and an optional note. "Change" opens it again, filled in. A refusal the server gives
 * (R2, N42's line, R4) shows in it, as served, and the dialog stays open. N42's line shows once
 * (REVIEW-24's U26): refused, it moves with its invite to where the dialog shows refusals.
 */
export function AskDialog({
  open,
  itemTitle,
  people,
  initialPerson,
  initialNote,
  pending,
  refusal,
  onAsk,
  onClose,
  invite,
}: {
  open: boolean;
  itemTitle: string;
  people: readonly Teammate[];
  initialPerson?: string;
  initialNote?: string;
  pending: boolean;
  refusal?: AskRefusal;
  onAsk: (userId: string, note: string) => void;
  onClose: () => void;
  invite?: { href: string; open: () => void };
}) {
  const [person, setPerson] = useState(initialPerson ?? '');
  const [note, setNote] = useState(initialNote ?? '');
  const accessRefused = refusal?.withoutAccess === true;
  const inviteLink =
    invite !== undefined ? (
      <>
        {' '}
        <a
          href={invite.href}
          onClick={(event) => {
            event.preventDefault();
            onClose();
            invite.open();
          }}
          className="underline underline-offset-2 hover:text-text-strong"
          {...testIdProp(`${REVIEW_PREFIX}-ask-invite`)}
        >
          {INVITE_TO_COMPLIANCE}
        </a>
      </>
    ) : null;
  return (
    <Dialog.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      {open && (
        <Dialog.Content
          width={480}
          className="flex flex-col gap-16"
          testId={`${REVIEW_PREFIX}-ask-dialog`}
        >
          <div className="flex flex-col gap-6">
            <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
              {ASK_A_TEAMMATE}
            </Dialog.Title>
            <Dialog.Description className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
              {itemTitle}
            </Dialog.Description>
          </div>
          <div className="flex flex-col gap-6">
            <Select
              appearance="saas"
              label={ASK_WHO}
              value={person.length > 0 ? person : null}
              onValueChange={(next) => setPerson(next ?? '')}
              options={people.map((teammate) => ({ value: teammate.userId, label: teammate.name }))}
              placeholder={CHOOSE_A_PERSON}
              testId={`${REVIEW_PREFIX}-ask-who`}
            />
            {!accessRefused && (
              <p className="text-s12 leading-h15 text-text-tertiary">
                {ASK_ACCESS_LINE}
                {inviteLink}
              </p>
            )}
          </div>
          <Textarea
            label={ASK_NOTE}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            rows={3}
            testId={`${REVIEW_PREFIX}-ask-note`}
          />
          {refusal !== undefined && (
            <p
              className="text-s12 leading-h15 text-text"
              role="alert"
              {...testIdProp(`${REVIEW_PREFIX}-ask-refusal`)}
            >
              {refusal.line}
              {accessRefused && inviteLink}
            </p>
          )}
          <div className="flex justify-end gap-10">
            <Button
              appearance="saas"
              tone="secondary"
              type="button"
              onClick={onClose}
              testId={`${REVIEW_PREFIX}-ask-cancel`}
            >
              {CANCEL}
            </Button>
            <Button
              appearance="saas"
              tone="primary"
              type="button"
              disabled={person.length === 0}
              loading={pending}
              onClick={() => onAsk(person, note)}
              testId={`${REVIEW_PREFIX}-ask-confirm`}
            >
              {ASK}
            </Button>
          </div>
        </Dialog.Content>
      )}
    </Dialog.Root>
  );
}

/** A card's last row: its choice on the left, room on the right for what a later row puts there. */
export function ActionRow({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-16 gap-y-8">{children}</div>
  );
}

/** The panel's grey date card: the date or its words, the basis, the quiet line, the source. */
export function DateBlock({ card, testId }: { card: DateCard; testId: string }) {
  return (
    <div
      className="flex flex-col gap-4 rounded-lg bg-surface-sunken px-16 py-12"
      {...testIdProp(testId)}
    >
      <p
        className={cn(
          card.dated
            ? 'text-s16 font-w600 tabular-nums text-text-strong'
            : 'text-s14 font-w600 text-text-secondary',
        )}
      >
        {card.lead}
        {/* CCM-R1-INTAKE-MDEXT-OPEN: N1, as the add sheet's card shows it. */}
        {card.leadNote !== undefined && (
          <span className="text-s13 font-w400 text-text-secondary">
            {' · '}
            {card.leadNote}
          </span>
        )}
      </p>
      {card.yearLine !== undefined && (
        <p className="text-s13 leading-h15 text-text-secondary">{card.yearLine}</p>
      )}
      {card.basis !== undefined && (
        <p className="text-s13 leading-h15 text-text-secondary">{card.basis}</p>
      )}
      {card.firstPeriod !== undefined && (
        <p className="text-s13 leading-h15 text-text-secondary">{card.firstPeriod}</p>
      )}
      {card.aside !== undefined && (
        <p className="text-s12 leading-h15 text-text-tertiary">{card.aside}</p>
      )}
      <div className="pt-4">
        <RuleProvenanceLine provenance={card.provenance} />
      </div>
    </div>
  );
}

/** The facts a result read: "Based on", then each with who recorded it, when, and "Correct". */
export function FactList({
  facts,
  onCorrect,
  testId,
}: {
  facts: readonly FactLine[];
  /** Absent for someone who can only view: nothing to correct. */
  onCorrect?: (fact: FactLine) => void;
  testId: string;
}) {
  if (facts.length === 0) return null;
  return (
    <div className="flex flex-col gap-4" {...testIdProp(testId)}>
      <p className="text-s12 font-w600 text-text-secondary">{BASED_ON}</p>
      <ul className="flex flex-col gap-2">
        {facts.map((fact) => (
          <li key={fact.key} className="text-s13 leading-h15 text-text">
            {fact.label}: <span className="font-w600">{fact.value}</span>
            {fact.source !== undefined && (
              <span className="text-text-tertiary">
                {' · '}
                {fact.source}
              </span>
            )}
            {(fact.by !== undefined || fact.on !== undefined) && (
              <span className="text-text-tertiary">
                {' · '}
                {/* A name and a date each keep to one line (cut 1008-125's walk). */}
                {[fact.by, fact.on]
                  .filter((part): part is string => part !== undefined)
                  .map((part, index) => (
                    <Fragment key={part}>
                      {index > 0 && ', '}
                      <span className="whitespace-nowrap">{part}</span>
                    </Fragment>
                  ))}
              </span>
            )}
            {fact.correctAt !== undefined && onCorrect !== undefined && (
              <>
                {' · '}
                <TextAction
                  onClick={() => onCorrect(fact)}
                  testId={`${testId}-correct-${fact.correctAt}`}
                >
                  {CORRECT}
                </TextAction>
              </>
            )}
            {fact.changed === true && (
              <span className="ml-8">
                <MarkTag mark="changed" />
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** "Show how Givmo got this": each condition in its words and how it came out (N9, N49, N50). */
export function BasisToggle({ basis, testId }: { basis: Basis; testId: string }) {
  const [open, setOpen] = useState(false);
  if (basis.conditions.length === 0) return null;
  return (
    <div className="flex flex-col gap-8">
      <button
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        className="flex w-fit items-center gap-4 rounded-xs text-s13 text-text-secondary hover:text-text-strong"
        {...testIdProp(testId)}
      >
        {open ? HIDE_HOW : SHOW_HOW}
        <Icon icon={open ? ChevronUp : ChevronDown} size={14} aria-hidden />
      </button>
      {open && (
        <div
          className="flex flex-col gap-8 border-l-2 border-border-subtle pl-12"
          {...testIdProp(`${testId}-body`)}
        >
          <ul className="flex flex-col gap-6">
            {basis.conditions.map((condition, index) => (
              <li key={`${index}:${condition.words}`} className="text-s13 leading-h15 text-text">
                {/* The outcome keeps to its line's last word (cut 1008-125's walk). */}
                {condition.words}:{'\u00a0'}
                <span className="whitespace-nowrap font-w600">
                  {OUTCOME_WORDS[condition.outcome] ?? condition.outcome}
                </span>
              </li>
            ))}
          </ul>
          <p className="text-s12 leading-h15 text-text-tertiary">
            {evaluatedOn(basis.evaluatedOn)}
          </p>
        </div>
      )}
    </div>
  );
}

/** N24: the evidence the met result's source may ask for, in its own sentence, with its citation. */
export function EvidenceNote({ line, testId }: { line: EvidenceLine; testId: string }) {
  return (
    <p className="text-s12 leading-h15 text-text-secondary" {...testIdProp(testId)}>
      {line.lead} {line.quote}
      {/* Its citation on a line of its own, under the quotation, lighter (frames 97 and 105). */}
      {line.citation !== undefined && (
        <span className="block text-text-tertiary">{line.citation}</span>
      )}
    </p>
  );
}

// ── Answer controls (N52; the profile's own fields) ────────────────────────────────────────────

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const DAYS_IN_MONTH = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

/** Month and day, as the panel's fiscal-year-end form asks them, with N52's placeholders. */
export function MonthDayPicker({
  month,
  day,
  onChange,
  disabled,
  testId,
}: {
  month: string;
  day: string;
  onChange: (next: { month: string; day: string }) => void;
  disabled?: boolean;
  testId: string;
}) {
  const days = month === '' ? 31 : (DAYS_IN_MONTH[Number(month) - 1] ?? 31);
  return (
    <div className="flex gap-8" role="group" {...testIdProp(testId)}>
      <Select
        appearance="saas"
        aria-label={MONTH}
        placeholder={MONTH}
        className="w-[180px]"
        value={month === '' ? null : month}
        disabled={disabled}
        onValueChange={(next) => {
          const chosen = next ?? '';
          const limit = chosen === '' ? 31 : (DAYS_IN_MONTH[Number(chosen) - 1] ?? 31);
          onChange({ month: chosen, day: day !== '' && Number(day) > limit ? '' : day });
        }}
        options={MONTHS.map((name, index) => ({ value: String(index + 1), label: name }))}
        testId={`${testId}-month`}
      />
      <Select
        appearance="saas"
        aria-label={DAY}
        placeholder={DAY}
        className="w-[96px]"
        value={day === '' ? null : day}
        disabled={disabled}
        onValueChange={(next) => onChange({ month, day: next ?? '' })}
        options={Array.from({ length: days }, (_, index) => ({
          value: String(index + 1),
          label: String(index + 1),
        }))}
        testId={`${testId}-day`}
      />
    </div>
  );
}

/**
 * The states a question asks about, chosen in a list: the Select's look ("Choose states", N52), a
 * checkbox per state. The chosen states read as their names, in the list's order.
 */
export function StatesPicker({
  codes,
  onChange,
  disabled,
  label,
  testId,
}: {
  codes: readonly string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  label: string;
  testId: string;
}) {
  const chosen = US_JURISDICTIONS.filter((entry) => codes.includes(entry.code));
  return (
    <Popover.Root>
      <Popover.Trigger
        disabled={disabled}
        aria-label={label}
        className={cn(
          'flex h-36 w-[320px] items-center justify-between gap-8 rounded-md border bg-surface px-12',
          'font-sans text-s14 font-w500 tracking-normal text-text outline-none transition-[box-shadow,border-color]',
          'border-neutral-500 hover:border-neutral-600 focus-visible:border-brand-500 focus-visible:shadow-ring',
          'data-[popup-open]:border-brand-500 data-[popup-open]:shadow-ring disabled:cursor-not-allowed disabled:opacity-50',
        )}
        {...testIdProp(testId)}
      >
        <span className={cn('truncate', chosen.length === 0 && 'text-text-placeholder')}>
          {chosen.length === 0 ? CHOOSE_STATES : chosen.map((entry) => entry.name).join(', ')}
        </span>
        <Icon icon={ChevronDown} size={16} className="shrink-0 text-icon-muted" aria-hidden />
      </Popover.Trigger>
      <Popover.Content
        saas
        sideOffset={6}
        className="max-h-[280px] w-[320px] overflow-y-auto p-4"
        testId={`${testId}-list`}
      >
        <ul className="flex flex-col">
          {US_JURISDICTIONS.map((entry) => {
            const checked = codes.includes(entry.code);
            return (
              <li key={entry.code}>
                <label className="flex cursor-pointer items-center gap-8 rounded-md px-8 py-6 text-s14 text-text hover:bg-surface-hover">
                  <Checkbox
                    appearance="saas"
                    checked={checked}
                    onCheckedChange={() =>
                      onChange(
                        checked
                          ? codes.filter((code) => code !== entry.code)
                          : [...codes, entry.code],
                      )
                    }
                    testId={`${testId}-${entry.code}`}
                  />
                  {entry.name}
                </label>
              </li>
            );
          })}
        </ul>
      </Popover.Content>
    </Popover.Root>
  );
}
